import { storageKeys } from '@/constants/storage';
import {
  BNPL_EXPERIMENT_VARIANTS,
  type BnplExperimentVariant,
  type LdEntryType,
} from '@/utils/tracking/ldEvents';

import { BNPL_METHODS, BNPL_SESSION_TTL_MS } from './bnpl';
import type { BnplMethodType } from './bnpl';
import { createPaymentReturnStorage } from './paymentReturnStorage';
import type { StorageAdapter } from './paymentReturnStorage';

export type BnplPaymentParams = {
  methodType: BnplMethodType;
  paymentMethodId: string;
  shopId: string;
  shopRoute?: string;
  cartId: string;
  barberId?: string;
  promoId?: string;
  paymentIntentId?: string;
  clientSecret?: string;
  status?: string;
  createdAt: number;
  // gift card fields
  amountCents?: number;
  currency?: string;
  isGiftCard?: boolean;
  /**
   * @deprecated One-release migration fallback only. The shared
   * `ldCheckoutContext` snapshot (written at Book press) is now the source of
   * truth for BNPL experiment context. These are read only for a redirect
   * that departed before that snapshot shipped and has no other way to report
   * its result. Stop writing them; delete once no in-flight redirect can
   * predate the `ldCheckoutContext` deploy.
   */
  ldVariant?: BnplExperimentVariant;
  /** @deprecated See `ldVariant`. */
  ldEnrolled?: boolean;
  /** @deprecated See `ldVariant`. */
  ldEntryType?: LdEntryType;
  /** @deprecated See `ldVariant`. */
  ldOrderTotalDollars?: number;
};

function isBnplPaymentParams(data: unknown): data is BnplPaymentParams {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  return (
    BNPL_METHODS.includes(obj.methodType as BnplMethodType) &&
    typeof obj.paymentMethodId === 'string' &&
    typeof obj.shopId === 'string' &&
    (obj.shopRoute === undefined || typeof obj.shopRoute === 'string') &&
    typeof obj.cartId === 'string' &&
    (obj.barberId === undefined || typeof obj.barberId === 'string') &&
    (obj.promoId === undefined || typeof obj.promoId === 'string') &&
    (obj.paymentIntentId === undefined ||
      typeof obj.paymentIntentId === 'string') &&
    (obj.clientSecret === undefined || typeof obj.clientSecret === 'string') &&
    (obj.status === undefined || typeof obj.status === 'string') &&
    typeof obj.createdAt === 'number' &&
    (obj.amountCents === undefined || typeof obj.amountCents === 'number') &&
    (obj.currency === undefined || typeof obj.currency === 'string') &&
    (obj.isGiftCard === undefined || typeof obj.isGiftCard === 'boolean') &&
    (obj.ldVariant === undefined ||
      BNPL_EXPERIMENT_VARIANTS.includes(
        obj.ldVariant as BnplExperimentVariant,
      )) &&
    (obj.ldEnrolled === undefined || typeof obj.ldEnrolled === 'boolean') &&
    (obj.ldEntryType === undefined ||
      obj.ldEntryType === 'guest' ||
      obj.ldEntryType === 'authenticated') &&
    (obj.ldOrderTotalDollars === undefined ||
      typeof obj.ldOrderTotalDollars === 'number')
  );
}

const localStorageAdapter: StorageAdapter = {
  getItem: (key) => localStorage.getItem(key),
  setItem: (key, value) => localStorage.setItem(key, value),
  removeItem: (key) => localStorage.removeItem(key),
};

const bnplStorage = createPaymentReturnStorage(
  storageKeys.bnplPending,
  isBnplPaymentParams,
  localStorageAdapter,
  BNPL_SESSION_TTL_MS,
);

export const getBnplPaymentFromSession = bnplStorage.get;
export const setBnplPaymentFromSession = bnplStorage.set;
export const clearBnplPaymentFromSession = bnplStorage.clear;
